import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import baseUrl from '../../base_url';
import Sidebar from './components/sidebar';

const Dashboard = () => {
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalOrders: 0,
    totalCustomers: 0
  });
  
  // Check if admin is logged in
  useEffect(() => {
    const getUser = async() => {
      try {
        await axios.get(`${baseUrl}api/admin/checkLogin`, { withCredentials: true });
      } catch(err) {
        console.log("error while checking if logged in");
        console.log(err);
        navigate("/admin/login");
      }
    };
    getUser();
  }, [navigate, baseUrl]);
  
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}api/admin/get-orders`, { withCredentials: true });
        const usersResponse = await axios.get(`${baseUrl}api/admin/get-all-users`, { withCredentials: true })
        
        if (response?.data?.data && response?.data?.data?.length > 0) {
          setOrders(response.data.data);

          const totalAmount = response.data.data.reduce((sum, order) => sum + order.totalAmount, 0);
          const uniqueCustomers = new Set(response.data.data.map(order => order.user));
          
          setStats({
            totalSales: totalAmount,
            totalOrders: response.data.data.length,
            totalCustomers: usersResponse.data.data.length
          });
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [baseUrl]);
  
  const ordersToFulfill = orders.filter(order => 
    order.status === "Placed" || order.status === "Paid" || order.status === "Pending"
  );


  const unpaidOrders = orders.filter(order => 
    (order.paymentMethod && order.paymentMethod[0] === "COD") &&
    (order.status === "Placed" || order.status === "Awaiting Payment")
  );


  // Format date from ISO string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const year = date.getFullYear().toString().substr(-2);
    return `${day} ${month} ${year}`;
  };
  
  // Format time from ISO string
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric',
      minute: '2-digit',
      hour12: true 
    }).toLowerCase();
  };


  return (
    <div className="flex h-screen bg-gray-100">

      <Sidebar/>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Navbar */}


        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-gray-100">
          <h1 className="text-2xl font-semibold text-gray-700 mb-6">Overview Dashboard</h1>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-lg text-gray-500">Loading dashboard data...</div>
            </div>
          ) : (
            <>
              {/* Stat Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white p-6 rounded-md shadow">
                  <div className="text-sm font-medium text-gray-500 mb-2">TOTAL SALES</div>
                  <div className="text-2xl font-bold text-blue-900">₹ {stats.totalSales.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  <div className="mt-4 text-right">
                    <a href="#" className="text-blue-500 text-sm">View Reports</a>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-md shadow">
                  <div className="text-sm font-medium text-gray-500 mb-2">TOTAL ORDERS</div>
                  <div className="text-2xl font-bold text-blue-900">{stats.totalOrders}</div>
                  <div className="mt-4 text-right">
                    <a href="#" className="text-blue-500 text-sm">View Reports</a>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-md shadow">
                  <div className="text-sm font-medium text-gray-500 mb-2">TOTAL CUSTOMERS</div>
                  <div className="text-2xl font-bold text-blue-900">{stats.totalCustomers}</div>
                  <div className="mt-4 text-right">
                    <a href="#" className="text-blue-500 text-sm">View Reports</a>
                  </div>
                </div>
              </div>

              {/* Orders to Fulfill */}
              <div className="bg-white rounded-md shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-900 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8z" />
                      <path d="M12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                    </svg>
                    <span className="font-medium">{ordersToFulfill.length} Orders to Fulfill</span>
                  </div>
                  <button className="text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ordersToFulfill.slice(0, 5).map((order) => {
                        // Get the first address if available
                        const address = order.address && order.address.length > 0 
                          ? `${order.address[0].streetAddress}, ${order.address[0].city}`
                          : 'No address provided';
                        
                        // Calculate total quantity
                        const totalQuantity = order.cart.reduce((sum, item) => sum + item.quantity, 0);
                        
                        return (
                          <tr key={order._id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(order.createdAt)} {formatTime(order.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{address}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{totalQuantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">₹ {order.totalAmount.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-6 py-3 text-right">
                    <a href="/admin/orders" className="text-blue-500 text-sm">View All</a>
                  </div>
                </div>
              </div>

              {/* Unpaid Orders */}
              <div className="bg-white rounded-md shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-900 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{unpaidOrders.length} Unpaid Orders</span>
                  </div>
                  <button className="text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {unpaidOrders.slice(0, 5).map((order) => {
                        // Get short order ID (last 7 characters)
                        const shortOrderId = `#${order._id.substring(order._id.length - 7)}`;
                        
                        // Get payment type (default to COD if not specified)
                        const paymentType = order.paymentMethods && order.paymentMethods.length > 0
                          ? order.paymentMethods[0]
                          : "COD";

                        const address = order.address && order.address.length > 0 
                          ? `${order.address[0].streetAddress}, ${order.address[0].city}`
                          : 'No address provided';

                         const totalQuantity = order.cart.reduce((sum, item) => sum + item.quantity, 0);

                        const formatDate = (dateString) => {
                          const date = new Date(dateString);
                          const day = date.getDate();
                          const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
                          const year = date.getFullYear().toString().substr(-2);
                          return `${day} ${month} ${year}`;
                        };
                        
                        // Format time from ISO string
                        const formatTime = (dateString) => {
                          const date = new Date(dateString);
                          return date.toLocaleTimeString('en-US', { 
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true 
                          }).toLowerCase();
                        };
                          
                        return (
                          <tr key={order._id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(order.createdAt)} {formatTime(order.createdAt)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {address}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{totalQuantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">₹ {order.totalAmount.toLocaleString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-6 py-3 text-right">
                    <a href="#" className="text-blue-500 text-sm">View All</a>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;